import Axios from 'axios';
import { getCookie } from 'utils/cookies';
import { wrapAxios, ZipkinTracer } from 'zipkin-tracer';
import { IGenericObject } from 'types';

const NEP_ORGANIZATION = 'nep-organization';
const POST = 'post';
const DELETE = 'delete';
const AUTH_URL = '/api/principal';
const CATALOG_ITEM_URL = '/api/catalog/items/'
const CATALOG_ALL_CATEGORIES_URL = '/api/catalog/categories?'
const CATALOG_CATEGORIES_ANCESTORS_URL = '/api/catalog/categories/'
const ITEM_GROUPS_URL = '/api/item-groups'
const BUSINNES_ERRORS = [404, 409];
const AUTH_ERRORS = [401, 403];

export interface IResponseError {
  status: number;
  message: string;
  caption?: string;
  severity?: string;
  errorCode: string;
}

export interface IHttpResponse {
  success: boolean;
  data?: any;
  error?: IResponseError;
}

export const api =
  process.env.REACT_APP_ZIPKIN !== 'false' && process.env.NODE_ENV !== 'test'
    ? wrapAxios(
        Axios.create({
          baseURL: process.env.REACT_APP_SERVER_URL,
        }),
        {
          tracer: ZipkinTracer,
        }
      )
    : Axios.create({
        baseURL: process.env.REACT_APP_SERVER_URL,
      });

api.defaults.headers.post['Content-Type'] = 'application/json';

api.interceptors.request.use((config) => {
  const { url, method, data } = config;
  if (url === AUTH_URL) {
    if (method === POST) {
      document.cookie = `${NEP_ORGANIZATION}=${data.organization};path=/;samesite=strict`;
    } else if (method === DELETE) {
      document.cookie = `${NEP_ORGANIZATION}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
    }
  }
  config.headers[NEP_ORGANIZATION] = getCookie(NEP_ORGANIZATION);
  return config;
});

api.interceptors.response.use(
  (response) => handleSuccess(response),
  (error) => handleError(error)
);

const handleSuccess = (response: any) => {
  return response;
};

const handleError = (error: any) => {
  const isLoginPage = error.response?.config.url.includes(AUTH_URL);
  const status = error.response.status;
  const url = error.response?.config.url;
  const isCatalogNotFoundError = (url.includes(CATALOG_ITEM_URL) || url.includes(CATALOG_CATEGORIES_ANCESTORS_URL)) &&  status === 404;
  const isCatalogServiceError = status >= 500 &&
    (url.includes(CATALOG_ALL_CATEGORIES_URL) || url.includes(CATALOG_CATEGORIES_ANCESTORS_URL) || url.includes(CATALOG_ITEM_URL) || url.includes(ITEM_GROUPS_URL))
  if (status === 401 && !isLoginPage) {
    error.message = 'User Session Expired';
    error.authError = true;
    return Promise.reject(error);
  }
  if(isCatalogNotFoundError || isCatalogServiceError){
    error.dismissDialog = true;
    error.status = error.response.status;
    return Promise.reject(error);
  }

  if (Array.isArray(error.response.data)) {
    const firstError = error.response.data[0];
    const correlationId = error.response.headers['nep-correlation-id'];
    const errorMessage = BUSINNES_ERRORS.includes(status)
      ? firstError.description : AUTH_ERRORS.includes(status) && isLoginPage ?
        firstError.description: status === 503 && isLoginPage ?'The server is temporarily unavailable'
          :`An error has occurred. Please contact system administrator.${
          correlationId ? ` CorrelationID: ${correlationId}` : ''
        }`;
    const errorResp = {
      status,
      message: errorMessage,
      caption: firstError.caption ? firstError.caption : '',
      severity: firstError.severity ? firstError.severity : '',
      errorCode: firstError.errorCode,
      isLoginPage,
    };

    return Promise.reject(errorResp);
  }

  return Promise.reject(Object.assign(error, { isLoginPage: isLoginPage }));
};

export const get = async (url: string, config?): Promise<IHttpResponse> => {
  try {
    const result = await api.get(url, config);
    return { success: true, data: result.data };
  } catch (error) {
    return { success: false, error };
  }
};

export const post = async (
  url: string,
  body: any,
  config?: IGenericObject
): Promise<IHttpResponse> => {
  try {
    const result = await api.post(url, body, config);
    return { success: true, data: result.data };
  } catch (error) {
    return { success: false, error };
  }
};

export const put = async (
  url: string,
  body: any,
  config?: IGenericObject
): Promise<IHttpResponse> => {
  try {
    const result = await api.put(url, body, config);
    return { success: true, data: result.data };
  } catch (error) {
    return { success: false, error };
  }
};

export const remove = async (url: string, config?): Promise<IHttpResponse> => {
  try {
    const result = await api.delete(url, config);
    return { success: true, data: result.data };
  } catch (error) {
    return { success: false, error };
  }
};
